import { useEffect } from 'react';

import { useSelector } from 'react-redux';

import * as Sentry from '@sentry/nextjs';
import { useCookies } from 'react-cookie';

import { i18n } from '@i18n';

import { LOCAL_STORAGE_KEY } from '@framework/constants/app';
import { APPLICATION_SECTIONS, ATTENDEE_SECTIONS } from '@framework/constants/enums';
import { describeMemberRole } from '@framework/members';
import { I18N_NEXT_COOKIE } from '@framework/utils/cookies';
import { transformLanguageCode } from '@framework/utils/languages';

import useActivityDetector from '@hooks/useActivityDetector';
import useAuthenticationStatus from '@hooks/useAuthenticationStatus';
import useLastVisited from '@hooks/useLastVisited';
import useLoggedMember from '@hooks/useLoggedMember';
import useLoggedUser from '@hooks/useLoggedUser';

import { getCurrentAppSection } from '@redux/selectors/AppSelector';

const Application = () => {
    const currentSection = useSelector(getCurrentAppSection);
    const [cookies, setCookie] = useCookies([I18N_NEXT_COOKIE]);
    const languageInCookie = cookies?.[I18N_NEXT_COOKIE];
    const { data: proofStatus } = useAuthenticationStatus();
    const { data: user } = useLoggedUser({
        enabled: Boolean(proofStatus?.isAuthenticated)
    });
    const { data: member } = useLoggedMember({
        enabled: Boolean(proofStatus?.isAuthenticated)
    });
    useActivityDetector();
    useLastVisited();

    useEffect(() => {
        if (
            currentSection &&
            currentSection !== ATTENDEE_SECTIONS.MEETING_DETAILS &&
            currentSection !== APPLICATION_SECTIONS.MEETING_DETAILS
        ) {
            // remove stored edit mode tab when navigating to non-meeting details views
            localStorage.removeItem(LOCAL_STORAGE_KEY.MEETING_EDIT_MODE_TAB);
        }
    }, [currentSection]);

    useEffect(() => {
        if (user?.id) {
            const languageCode = transformLanguageCode(user.language);
            if (languageInCookie !== languageCode) {
                setCookie?.(I18N_NEXT_COOKIE, languageCode, { path: '/' });
                i18n?.reloadResources?.(languageCode);
                i18n?.changeLanguage?.(languageCode);
            }
        }
    }, [user, languageInCookie]);

    useEffect(() => {
        if (member?.id) {
            Sentry.getCurrentScope().setUser({
                id: member.id,
                roomId: member.roomId,
                role: describeMemberRole({
                    role: member.role,
                    isMeetingOrganiser: member.isMeetingOrganiser,
                    isGroupManager: member.isGroupManager
                })
            });
        }
    }, [member]);

    return null;
};

export default Application;
